import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { hasAuthCookie } from '@quintoandar/wat-cookie';
import CozyLogo from '@quintoandar/cozy-core/CozyLogo';
import Button from '@quintoandar/cozy-core/Button';
import Typography from '@quintoandar/cozy-core/Typography';
import tokens from '@quintoandar/cozy-theme/tokens/js';

import { AGENTS_API_URL } from 'config';

const DEFAULT_EMAIL =
  process.env.APP2_AUTH_EMAIL || 'andre.admin@app2.local';
const DEFAULT_REDIRECT = '/agenda';

const Page = styled.main`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${tokens.alias.color.surface.default};
  padding: ${tokens.alias.spacing.medium};
`;

const GridWrap = styled.section`
  width: 100%;
  max-width: 1032px;
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProductMark = styled.div`
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border-radius: ${tokens.alias.shape.border.radius.small};
  background: #0b5cff;
  border: 1px solid ${tokens.alias.color.border.default};
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
`;

const AuthPanel = styled.section`
  width: 100%;
  max-width: 380px;
`;

const Shell = styled.div`
  width: 100%;
  display: grid;
  gap: ${tokens.alias.spacing.medium};
`;

const Form = styled.form`
  display: grid;
  gap: ${tokens.alias.spacing.small};
`;

const HeaderStack = styled.div`
  display: grid;
  gap: ${tokens.alias.spacing.xxsmall};
`;

const LogoWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${tokens.alias.spacing.small};
`;

const Field = styled.label`
  display: grid;
  gap: ${tokens.alias.spacing.hxsmall};
`;

const FieldText = styled(Typography)`
  font-weight: ${tokens.alias.typography.bodyStrong.fontWeight};
`;

const AuthInput = styled.input`
  width: 100%;
  min-height: 52px;
  box-sizing: border-box;
  appearance: none;
  background: ${tokens.alias.color.surface.default};
  border: 1px solid ${tokens.alias.color.border.default};
  border-radius: ${tokens.alias.shape.border.radius.small};
  color: ${tokens.alias.color.text.default};
  font-family: ${tokens.alias.typography.fontFamily};
  font-size: ${tokens.alias.typography.body.fontSize};
  line-height: ${tokens.alias.typography.body.lineHeight};
  outline: none;
  padding: 0 ${tokens.alias.spacing.xsmall};
  transition:
    border-color 160ms ease,
    box-shadow 160ms ease;

  &:hover {
    border-color: ${tokens.alias.color.border.hovered};
  }

  &:focus {
    border-color: ${tokens.alias.color.border.interaction.default};
    box-shadow: ${tokens.alias.color.border.focusVisible} 0 0 0 2px;
  }
`;

const ErrorBox = styled.div`
  background: ${tokens.alias.color.surface.critical.lighter.default};
  border-radius: ${tokens.alias.shape.border.radius.small};
  color: ${tokens.alias.color.text.critical.default};
  padding: ${tokens.alias.spacing.xsmall};
`;

const FooterText = styled(Typography)`
  display: block;
  margin-top: ${tokens.alias.spacing.medium};
`;

const TextLink = styled.a`
  color: ${tokens.alias.color.text.interaction.default};
  display: inline-block;
  font-weight: ${tokens.alias.typography.bodyStrong.fontWeight};
  margin-top: ${tokens.alias.spacing.small};
  text-decoration: none;
`;

function safeRedirectFromQuery() {
  const params = new URLSearchParams(window.location.search);
  const redirect = params.get('redirect');

  if (!redirect) return DEFAULT_REDIRECT;

  try {
    const parsed = new URL(redirect, window.location.origin);
    const isSameOrigin = parsed.origin === window.location.origin;
    const isAuthRoute =
      parsed.pathname === '/auth/login' || parsed.pathname === '/auth/logout';
    const isForbiddenRoute = parsed.pathname === '/forbidden';

    if (isSameOrigin && !isAuthRoute && !isForbiddenRoute) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  } catch (error) {
    void error;
    if (
      redirect.startsWith('/') &&
      !redirect.startsWith('//') &&
      redirect !== '/forbidden'
    ) {
      return redirect;
    }
  }

  return DEFAULT_REDIRECT;
}

async function parseResponse(response) {
  try {
    return await response.json();
  } catch (error) {
    void error;
    return {};
  }
}

export default function AuthLogin() {
  const redirectTo = useMemo(() => safeRedirectFromQuery(), []);
  const pathname = window.location.pathname;
  const isVerifyEmail = pathname === '/verify-email';
  const [email, setEmail] = useState(DEFAULT_EMAIL);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isVerifyEmail && hasAuthCookie()) {
      window.location.replace(redirectTo);
    }
  }, [isVerifyEmail, redirectTo]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (isVerifyEmail) {
      window.location.assign(
        `/reset-password?email=${encodeURIComponent(email.trim())}`,
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${AGENTS_API_URL}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          password,
        }),
      });
      const body = await parseResponse(response);

      if (!response.ok) {
        setError(
          body?.error?.message ||
            'Não foi possível entrar com os dados informados.',
        );
        return;
      }

      if (body.token && !hasAuthCookie()) {
        document.cookie = `5AJWT_AUTH=${body.token}; path=/; SameSite=Lax`;
      }

      window.location.replace(redirectTo);
    } catch (requestError) {
      void requestError;
      setError('Não foi possível conectar ao backend local.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailChange = (event) => setEmail(event.target.value);
  const handlePasswordChange = (event) => setPassword(event.target.value);

  return (
    <Page>
      <GridWrap>
        <AuthPanel>
          <Shell>
            <LogoWrap>
              <CozyLogo variant="complete" kind="default" product="QuintoAndar" />
              <ProductMark>CI</ProductMark>
            </LogoWrap>

            <HeaderStack>
              <Typography kind="default" level="heading">
                {isVerifyEmail ? 'Não sei minha senha' : 'Acesse sua conta'}
              </Typography>
              <Typography kind="lighter" level="body">
                {isVerifyEmail
                  ? 'Confirme seu e-mail para te ajudarmos a criar uma nova senha.'
                  : 'Entre com o e-mail e senha cadastrados para acessar a Central da Imobiliária.'}
              </Typography>
            </HeaderStack>

            <Form data-testid="local-auth-login" onSubmit={handleSubmit}>
              <Field htmlFor="auth-email">
                <FieldText kind="default" level="body">
                  E-mail*
                </FieldText>
                <AuthInput
                  autoComplete="email"
                  data-testid="auth-email"
                  id="auth-email"
                  name="email"
                  onChange={handleEmailChange}
                  placeholder="Preencha com o seu e-mail"
                  required
                  type="email"
                  value={email}
                />
              </Field>

              {isVerifyEmail ? null : (
                <Field htmlFor="auth-password">
                  <FieldText kind="default" level="body">
                    Senha*
                  </FieldText>
                  <AuthInput
                    autoComplete="current-password"
                    data-testid="auth-password"
                    id="auth-password"
                    name="password"
                    onChange={handlePasswordChange}
                    placeholder="Preencha com a sua senha"
                    required
                    type="password"
                    value={password}
                  />
                </Field>
              )}

              {error ? (
                <ErrorBox data-testid="auth-error">
                  <Typography kind="critical" level="body">
                    {error}
                  </Typography>
                </ErrorBox>
              ) : null}

              <Button
                data-testid="auth-submit"
                disabled={isSubmitting}
                fullWidth
                isLoading={isSubmitting}
                type="submit"
              >
                {isVerifyEmail ? 'Confirmar e-mail' : 'Acessar minha conta'}
              </Button>
            </Form>

            {isVerifyEmail ? (
              <TextLink href="/login">Voltar para tela de login</TextLink>
            ) : (
              <TextLink href="/verify-email">Não sei minha senha</TextLink>
            )}

            <FooterText kind="lighter" level="caption">
              Central da Imobiliária
            </FooterText>
          </Shell>
        </AuthPanel>
      </GridWrap>
    </Page>
  );
}
